import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Seo } from "@/components/seo/Seo";
import { resolveHeadRoute } from "@/lib/seo/route-head";
import { stripLocaleFromPath } from "@/i18n/locale-utils";

const SITE_NAME = "Bootpreneurs";

/**
 * Route-layer head manager. Mounted once above the route tree, so EVERY route
 * gets a title + description without opting in. Generic by construction: it
 * only knows the key namespace the registry resolves for the current route.
 *
 * Keyed on (route, resolved language) and re-run on i18next `initialized`
 * and `languageChanged`. Writes at priority 0, so a page-level <Seo> (which
 * may add dynamic titles, JSON-LD or OG images) always wins.
 */
export function RouteHead() {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();

  const [, setTick] = useState(0);
  useEffect(() => {
    const cb = () => setTick((n) => n + 1);
    i18n.on("languageChanged", cb);
    i18n.on("initialized", cb);
    return () => {
      i18n.off("languageChanged", cb);
      i18n.off("initialized", cb);
    };
  }, [i18n]);

  const { rest } = stripLocaleFromPath(pathname);
  const { ns, section, noindex } = resolveHeadRoute(rest);

  const has = (key: string) => i18n.exists(key);
  const sectionLabel = t(`head.sections.${section}`, { defaultValue: SITE_NAME });

  const title =
    ns && has(`${ns}.seoTitle`)
      ? t(`${ns}.seoTitle`)
      : `${sectionLabel} — ${SITE_NAME}`;

  const descKey = ns && has(`${ns}.seoDescription`)
    ? `${ns}.seoDescription`
    : ns && has(`${ns}.seoDesc`)
      ? `${ns}.seoDesc`
      : null;

  const description = descKey ? t(descKey) : t("head.defaultDescription");

  return (
    <Seo
      key={`${rest}|${i18n.language}`}
      title={title}
      description={description}
      noindex={noindex}
      priority={0}
    />
  );
}
